// Components==============
import { ArrayElement } from 'typings';
import { useRedux } from 'hooks/useRedux';
import { useTranslate } from 'hooks/useTranslate';
import { sortOnName } from 'utilities/sortOnName';
import { usePermission } from 'hooks/usePermission';
// =========================

export type StudentListType = ReturnType<typeof useStudentList>;

export type StudentListEntity = ArrayElement<StudentListType>;

export function useStudentList(theme?: ThemeEntity) {
  const userId = useRedux((state) => state.authentication?.user?.id);
  const translate = useTranslate();
  const isTeacher = usePermission(['Docent', 'super-admin', 'Extern']);

  const studentList = useRedux((state) => {
    const list = state.project?.entities.allIds
      .map((id) => state.project?.entities.byId[id])
      .filter(
        (p) =>
          p?.theme_id === theme?.id &&
          (p?.created_by === userId ||
            (isTeacher &&
              p?.members.map((m) => m.user_id).includes(userId || 0)))
      );

    if (!isTeacher || (list?.length || 0) <= 1) return [];

    const myProcess = (list
      ?.map((p) => {
        const membership = p?.members.find((m) => m.user_id === userId)
          ?.membership;

        const isMyProcess = membership === 'admin' || membership === 'owner';

        return isMyProcess
          ? {
              name: translate('project.terms.my-process'),
              user: p?.author,
              memberId: p?.members.find((m) => m.user_id === userId)?.id,
              projectId: p?.id,
              userId: p?.author?.id,
            }
          : undefined;
      })
      .filter((exists) => exists) || []) as {
      name: string;
      user: UserEntity;
      memberId: number;
      projectId: number;
      userId: number;
    }[];

    const students = list
      ?.filter((p) => {
        const membership = p?.members.find((m) => m.user_id === userId)
          ?.membership;

        const notAStudent = membership !== 'owner' && membership !== 'admin';

        return notAStudent;
      })
      .map((p) => {
        const membersCount =
          p?.members.filter(
            (m) =>
              m.membership !== 'teacher' &&
              m.membership !== 'owner' &&
              m.membership !== 'extern' &&
              m.membership !== 'invited'
          ).length || 0;

        if (membersCount)
          return {
            name: `${translate('project.terms.group-of')} ${
              p?.author?.first_name
            } ${p?.author?.last_name} (+${membersCount})`,
            user: p?.author,
            // member id of current user in that group
            memberId: p?.members.find((m) => m.user_id === userId)?.id,
            projectId: p?.id,
            userId: p?.author?.id,
          };

        return {
          name: `${p?.author?.first_name} ${p?.author?.last_name}`,
          user: p?.author,
          // member id of current user in that group
          memberId: p?.members.find((m) => m.user_id === userId)?.id,
          projectId: p?.id,
          userId: p?.author?.id,
        };
      }) as {
      name: string;
      user: UserEntity;
      memberId: number;
      projectId: number;
      userId: number;
    }[];

    const sortedStudents = sortOnName(students);

    return [...myProcess, ...sortedStudents];
  });

  return studentList;
}
